import React, { useEffect, useRef, useState } from 'react';
import { Col, Flex, Image, Row, Timeline, Typography } from 'antd';
import { Card } from '../Card';
import { Heading } from '../Heading';
import './OrderTracker.css';
import { LOCAL_EN, StatusesEnum, StatusesType } from '@/config'; // Import the statuses
import { useTranslation } from 'react-i18next';
import { ActivityItem } from '@/types';
import { formatDateByDayjs } from '@/utils';

const { Text } = Typography;

interface ImageItem {
  imageTitle: string;
  imageUrl: string;
}

export interface TimelineItemProps extends ActivityItem {
  //TODO: TimelineItemProps should be removed
  day?: string;
  month?: string;
  time?: string;
  title: string;
  description?: string; // Make description optional
  images?: ImageItem[]; // Add images array
  status?: StatusesType; // Use the imported status type
}

interface OrderTrackerProps {
  timelineItems: TimelineItemProps[];
  hasHeightLimitation?: boolean;
  leftSectionHeight?: number;
}

const OrderTracker: React.FC<OrderTrackerProps> = ({
  timelineItems,
  hasHeightLimitation,
  leftSectionHeight,
}) => {
  const wrapperElement = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation();

  const getStatusClassName = (status: StatusesEnum) => {
    switch (status) {
      case StatusesEnum.IN_PROGRESS:
        return ` ${i18n.language === LOCAL_EN ? 'custom-gray-circle-timeline-dot-rtl' : 'custom-gray-circle-timeline-dot-rtl'}`;
      case StatusesEnum.DELIVERED:
        return ` ${i18n.language === LOCAL_EN ? 'custom-green-circle-timeline-dot-ltr' : 'custom-green-circle-timeline-dot-rtl'}`;
      case StatusesEnum.ON_HOLD:
        return ` ${i18n.language === LOCAL_EN ? 'custom-yellow-circle-timeline-dot-ltr' : 'custom-yellow-circle-timeline-dot-rtl'}`;
      case StatusesEnum.CREATED:
        return ` ${i18n.language === LOCAL_EN ? 'custom-square-timeline-dot-ltr' : 'custom-square-timeline-dot-rtl'}`;
      default:
        return ' ';
    }
  };

  const getClassNames = () => {
    const heightLimitClasses =
      timelineItems.length > 6 && hasHeightLimitation ? `overflow-y-auto py-5` : '';

    const directionClasses =
      i18n.language === LOCAL_EN ? 'timeline_container-ltr' : 'timeline_container-rtl';
    return heightLimitClasses + ' ' + directionClasses;
  };

  const getHeight = (totalHeight: number) => {
    const minHeight = timelineItems.length > 3 ? 400 : 200;
    const elementTop = wrapperElement.current
      ? wrapperElement.current.getBoundingClientRect().top + window.scrollY
      : 0;

    return timelineItems.length > 6 && hasHeightLimitation
      ? Math.max(totalHeight - elementTop, minHeight) + 'px'
      : 'auto';
  };

  return (
    <Row gutter={[24, 16]} className='mt-4 mb-5'>
      <Col
        ref={wrapperElement}
        lg={24}
        md={24}
        xs={24}
        className={getClassNames()}
        style={{
          height: getHeight(leftSectionHeight || 0),
        }}
      >
        <Timeline mode='left'>
          {timelineItems.map((item, index) => {
            const label = (
              <Flex vertical gap={6} key={index}>
                <Text className='text-center font-bold text-xs leading-3 tracking-[-0.32px] text-customDarkBrown'>
                  {formatDateByDayjs(item.created_at, 'DD')}
                </Text>
                <Text className='text-center font-normal text-xs leading-3 tracking-[-0.32px] text-customGrayShade5'>
                  {formatDateByDayjs(item.created_at, i18n.language === LOCAL_EN ? 'MMM' : 'MMMM')}
                </Text>
                <Text
                  className={`${i18n.language === LOCAL_EN ? 'mt-1' : 'mt-2'} mb-4 text-center font-normal text-xs leading-[10px] tracking-[-0.32px] text-customGrayShade5`}
                >
                  {formatDateByDayjs(item.created_at, 'hh:mm')}
                </Text>
              </Flex>
            );

            return (
              <Timeline.Item
                key={index}
                label={label}
                className={getStatusClassName(
                  index === 0 ? StatusesEnum.DELIVERED : StatusesEnum.IN_PROGRESS,
                )}
              >
                <Card>
                  <Heading
                    title={t(item.title)}
                    fontSize='text-base'
                    fontWeight='font-bold'
                    extraClassName='capitalize'
                  />
                  {item.description ? (
                    <Flex
                      vertical
                      className='bg-customGrayShade1 mt-3 rounded-[5px] border border-dashed border-customGrayShade4 lg:px-2 px-[9px] py-2'
                    >
                      <Text className=' font-normal capitalize text-xs leading-4 text-customTunaPrimary'>
                        {item.description}
                      </Text>
                    </Flex>
                  ) : item.images ? (
                    <Row className='mt-3 rounded-[5px]   px-[9px] py-2'>
                      {item.images.map((image, idx) => (
                        <Col lg={6} md={12} xs={24} key={idx} className='mb-2'>
                          <Flex vertical>
                            <Text className=' font-normal capitalize text-xs leading-[18px] text-customGrayShade5'>
                              {image.imageTitle}
                            </Text>
                            <Image
                              width={60}
                              height={60}
                              src={image.imageUrl}
                              alt={image.imageTitle}
                              className='rounded-[5px] w-full'
                              style={{ maxWidth: '100%', marginBottom: '4px' }}
                            />
                          </Flex>
                        </Col>
                      ))}
                    </Row>
                  ) : null}
                </Card>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Col>
    </Row>
  );
};

export default OrderTracker;
